import { AxiosError } from "axios";
import { MedosClient } from "../client/MedosClient";
import { ENDPOINTS } from "../constants/endpoints";
function toNumberOrNull(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function toStringOrNull(value) {
    return typeof value === "string" && value.trim().length > 0 ? value : null;
}
function toStringArrayOrNull(value) {
    if (!Array.isArray(value))
        return null;
    const items = value.filter((item) => typeof item === "string" && item.trim().length > 0);
    return items.length > 0 ? items : null;
}
function toStringOrArrayOrNull(value) {
    if (typeof value === "string" && value.trim().length > 0) {
        return [value.trim()];
    }
    return toStringArrayOrNull(value);
}
function toEducationArrayOrNull(value) {
    if (typeof value === "string" && value.trim().length > 0) {
        return [value.trim()];
    }
    if (!Array.isArray(value)) {
        return null;
    }
    const items = value
        .map((item) => {
        if (typeof item === "string" && item.trim().length > 0) {
            return item.trim();
        }
        if (item && typeof item === "object") {
            const entry = item;
            const degree = typeof entry.degree === "string" && entry.degree.trim().length > 0
                ? entry.degree.trim()
                : null;
            if (!degree)
                return null;
            const institute = typeof entry.institute === "string" &&
                entry.institute.trim().length > 0
                ? entry.institute.trim()
                : null;
            const year = typeof entry.year === "string" && entry.year.trim().length > 0
                ? entry.year.trim()
                : null;
            return [degree, institute, year].filter(Boolean).join(" — ");
        }
        return null;
    })
        .filter((item) => typeof item === "string" && item.length > 0);
    return items.length > 0 ? items : null;
}
export function normalizePublicProfile(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const profile = raw;
    if (profile.status === 404 && typeof profile.message === "string") {
        return null;
    }
    const profileId = toNumberOrNull(profile.profileId ?? profile.ownerId);
    const id = toNumberOrNull(profile.id) ?? profileId;
    if (id == null)
        return null;
    return {
        id,
        profileId,
        workspaceId: toNumberOrNull(profile.workspaceId),
        profileType: profile.profileType === "USER" || profile.profileType === "WORKSPACE"
            ? profile.profileType
            : null,
        name: toStringOrNull(profile.name ?? profile.fullName),
        email: toStringOrNull(profile.email),
        phoneNumber: toStringOrNull(profile.phoneNumber),
        specialization: toStringOrNull(profile.specialization),
        gender: profile.gender === "MALE" ||
            profile.gender === "FEMALE" ||
            profile.gender === "OTHER"
            ? profile.gender
            : null,
        experience: toNumberOrNull(profile.experience),
        qualification: toStringOrNull(profile.qualification),
        registrationNumber: toStringOrNull(profile.registrationNumber),
        registrationAuthority: toStringOrNull(profile.registrationAuthority),
        designation: toStringOrNull(profile.designation),
        prefix: toStringOrNull(profile.prefix),
        fullName: toStringOrNull(profile.fullName),
        websiteUrl: toStringOrNull(profile.websiteUrl ?? profile.website),
        education: toEducationArrayOrNull(profile.education),
        awards: toStringArrayOrNull(profile.awards),
        customServices: toStringArrayOrNull(profile.customServices),
        googleRating: toNumberOrNull(profile.googleRating),
        googleReviewUrl: toStringOrNull(profile.googleReviewUrl),
        profileSummary: toStringOrNull(profile.profileSummary ?? profile.professionalSummary),
        description: toStringOrNull(profile.description),
        profileImageUrl: toStringOrNull(profile.profileImageUrl ?? profile.profilePictureUrl),
        services: toStringArrayOrNull(profile.services),
        expertise: toStringArrayOrNull(profile.expertise),
        languages: toStringArrayOrNull(profile.languages),
        mode: toStringArrayOrNull(profile.mode),
    };
}
export const PublicProfileService = {
    async fetchProfileByProfileId(profileId, options) {
        const client = await MedosClient.ensureInitialized();
        try {
            const response = await client.get(ENDPOINTS.publicProfiles.byId(profileId), {
                params: {
                    profileType: options?.profileType ?? "USER",
                },
            });
            return normalizePublicProfile(response.data ?? response);
        }
        catch (err) {
            if (err instanceof AxiosError) {
                const status = err.response?.status;
                const body = err.response?.data;
                if (status === 404 ||
                    (body &&
                        typeof body === "object" &&
                        body.status === 404)) {
                    return null;
                }
            }
            throw err;
        }
    },
    fetchProfileById(id, options) {
        return this.fetchProfileByProfileId(id, options);
    },
};
export function collectWorkspacePublicProfiles(workspace) {
    const byOwnerId = new Map();
    const add = (raw) => {
        const profile = normalizePractitionerProfile(raw);
        if (!profile || profile.profileId == null)
            return;
        if (byOwnerId.has(profile.profileId))
            return;
        byOwnerId.set(profile.profileId, profile);
    };
    for (const raw of workspace.publicProfile?.p ?? [])
        add(raw);
    for (const address of workspace.addresses ?? []) {
        for (const doctor of address.doctors ?? [])
            add(doctor.publicProfile);
    }
    return Array.from(byOwnerId.values());
}
export function getWorkspacePublicProfile(workspace) {
    return normalizePublicProfile(workspace.publicProfile?.wpp ?? null);
}
function normalizePractitionerProfile(raw) {
    const profile = normalizePublicProfile(raw);
    if (!profile)
        return null;
    return profile.profileType == null
        ? { ...profile, profileType: "USER" }
        : profile;
}
